import { TILE_SIZE, type Avatar, type Facing } from '@sokdaq/protocol';
import { PALETTE } from './palette.js';

/**
 * 아바타를 코드로 그린다.
 *
 * 스프라이트 시트 없이 사각형만 쌓는다. 픽셀 아트라 이 방식으로도 충분히
 * 모양이 나오고, 색 조합만 바꿔 사람을 구분할 수 있다.
 *
 * 좌표는 발밑 중심이다. 발을 기준으로 삼아야 아바타가 타일 위에 서 있는
 * 것처럼 보이고, y 정렬로 앞뒤를 가릴 때도 자연스럽다.
 */

const T = TILE_SIZE;

/** 발밑 기준 위로 그려 올리는 높이. */
export const AVATAR_HEIGHT = 30;

export function drawAvatar(
  ctx: CanvasRenderingContext2D,
  footX: number,
  footY: number,
  avatar: Avatar,
  facing: Facing,
  walkPhase: number,
): void {
  const x = Math.round(footX);
  const y = Math.round(footY);

  // 그림자를 먼저. 발밑에 깔려야 땅에 붙어 보인다.
  ctx.fillStyle = PALETTE.shadow;
  ctx.beginPath();
  ctx.ellipse(x, y - 1, 9, 3.5, 0, 0, Math.PI * 2);
  ctx.fill();

  // 걷는 동안 다리를 번갈아 내민다. 두 프레임이면 충분히 걸어 보인다.
  const step = walkPhase > 0.5 ? 1 : 0;

  // 다리
  ctx.fillStyle = '#2c3038';
  ctx.fillRect(x - 5, y - 8 + step, 4, 8 - step);
  ctx.fillRect(x + 1, y - 8 + (1 - step), 4, 8 - (1 - step));

  // 몸통
  ctx.fillStyle = avatar.top;
  ctx.fillRect(x - 7, y - 19, 14, 12);

  // 팔
  ctx.fillStyle = shade(avatar.top, -0.15);
  if (facing === 'left') {
    ctx.fillRect(x - 9, y - 18, 3, 9);
  } else if (facing === 'right') {
    ctx.fillRect(x + 6, y - 18, 3, 9);
  } else {
    ctx.fillRect(x - 9, y - 18, 3, 9);
    ctx.fillRect(x + 6, y - 18, 3, 9);
  }

  // 머리
  ctx.fillStyle = avatar.skin;
  ctx.fillRect(x - 6, y - 30, 12, 12);

  // 머리카락. 뒤통수(up)일 때는 얼굴 전체를 덮는다.
  ctx.fillStyle = avatar.hair;
  ctx.fillRect(x - 7, y - 31, 14, 5);
  if (facing === 'up') {
    ctx.fillRect(x - 6, y - 30, 12, 10);
  } else {
    if (facing !== 'right') ctx.fillRect(x - 7, y - 31, 3, 9);
    if (facing !== 'left') ctx.fillRect(x + 4, y - 31, 3, 9);
  }

  drawFace(ctx, x, y, facing);
}

function drawFace(ctx: CanvasRenderingContext2D, x: number, y: number, facing: Facing): void {
  if (facing === 'up') return; // 뒤통수

  ctx.fillStyle = '#1a1d23';
  const eyeY = y - 25;

  switch (facing) {
    case 'down':
      ctx.fillRect(x - 4, eyeY, 2, 2);
      ctx.fillRect(x + 2, eyeY, 2, 2);
      return;
    case 'left':
      ctx.fillRect(x - 5, eyeY, 2, 2);
      return;
    case 'right':
      ctx.fillRect(x + 3, eyeY, 2, 2);
      return;
  }
}

/** 이름표. 아바타 머리 위에 작게. */
export function drawNameTag(
  ctx: CanvasRenderingContext2D,
  footX: number,
  footY: number,
  nickname: string,
  isYou: boolean,
  viewportTopPx: number,
): void {
  ctx.font = '9px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const width = ctx.measureText(nickname).width + 6;
  const x = Math.round(footX);
  // 맨 위 벽에 붙어 서면 카메라가 더 올라가지 못해 이름표가 화면 밖으로 나간다.
  const y = Math.max(Math.round(footY) - AVATAR_HEIGHT - 8, viewportTopPx + 7);

  ctx.fillStyle = PALETTE.nameBackdrop;
  ctx.fillRect(x - width / 2, y - 6, width, 12);

  ctx.fillStyle = isYou ? '#7dd3fc' : PALETTE.nameText;
  ctx.fillText(nickname, x, y);
}

/**
 * 말풍선.
 *
 * 아바타 위에 뜬다. 폭은 글자에 맞추되 상한을 둬서 긴 메시지가
 * 화면을 가로지르지 않게 한다 — 그건 클래식 뷰에서 읽으면 된다.
 */
export function drawSpeechBubble(
  ctx: CanvasRenderingContext2D,
  footX: number,
  footY: number,
  text: string,
  isWhisper: boolean,
  viewportTopPx: number,
): void {
  const maxWidth = T * 5;
  ctx.font = '10px system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  const lines = wrapText(ctx, text, maxWidth - 12);
  const lineHeight = 13;
  const width = Math.min(
    maxWidth,
    Math.max(...lines.map((line) => ctx.measureText(line).width)) + 12,
  );
  const height = lines.length * lineHeight + 8;

  const x = Math.round(footX - width / 2);
  const above = Math.round(footY) - AVATAR_HEIGHT - 22 - height;

  /*
   * 위에 자리가 없으면 아바타 아래로 뒤집는다.
   *
   * 맨 위 벽에 붙어 서면 카메라가 더 올라가지 못해서 말풍선이 화면 밖에
   * 그려진다. 말은 했는데 아무것도 안 보이는 상태가 된다 — 벽 쪽 자리는
   * 사람들이 실제로 자주 서는 곳이라 반드시 처리해야 한다.
   */
  const flipped = above < viewportTopPx + 2;
  const y = flipped ? Math.round(footY) + 8 : above;

  ctx.fillStyle = isWhisper ? PALETTE.whisperFill : PALETTE.bubbleFill;
  roundRect(ctx, x, y, width, height, 5);
  ctx.fill();

  // 꼬리는 아바타를 향한다.
  ctx.beginPath();
  if (flipped) {
    ctx.moveTo(footX - 4, y);
    ctx.lineTo(footX + 4, y);
    ctx.lineTo(footX, y - 5);
  } else {
    ctx.moveTo(footX - 4, y + height);
    ctx.lineTo(footX + 4, y + height);
    ctx.lineTo(footX, y + height + 5);
  }
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = isWhisper ? PALETTE.whisperText : PALETTE.bubbleText;
  lines.forEach((line, index) => {
    ctx.fillText(line, x + 6, y + 4 + index * lineHeight);
  });
}

/** 정수기 이모트처럼 짧게 뜨는 상태 표시. */
export function drawEmote(
  ctx: CanvasRenderingContext2D,
  footX: number,
  footY: number,
  text: string,
  viewportTopPx: number,
): void {
  ctx.font = '10px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const width = ctx.measureText(text).width + 10;
  const x = Math.round(footX);
  const y = Math.max(Math.round(footY) - AVATAR_HEIGHT - 22, viewportTopPx + 9);

  ctx.fillStyle = PALETTE.nameBackdrop;
  roundRect(ctx, x - width / 2, y - 8, width, 16, 8);
  ctx.fill();

  ctx.fillStyle = '#fde68a';
  ctx.fillText(text, x, y);
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  // 한국어는 공백이 드물어 단어 단위로 자르면 줄이 안 나뉜다. 글자 단위로 센다.
  const lines: string[] = [];
  let current = '';

  for (const char of text) {
    if (char === '\n') {
      lines.push(current);
      current = '';
      continue;
    }
    if (ctx.measureText(current + char).width > maxWidth && current !== '') {
      lines.push(current);
      current = char;
      continue;
    }
    current += char;
  }
  if (current !== '') lines.push(current);

  // 말풍선은 흘긋 보는 것이다. 길면 잘라서 클래식 뷰로 미룬다.
  if (lines.length > 4) return [...lines.slice(0, 4), '…'];
  return lines.length > 0 ? lines : [''];
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
}

/** #rrggbb를 밝게/어둡게. 팔 그늘처럼 같은 색의 변주가 필요할 때. */
function shade(hex: string, amount: number): string {
  const value = Number.parseInt(hex.slice(1), 16);
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));

  const r = clamp(((value >> 16) & 0xff) * (1 + amount));
  const g = clamp(((value >> 8) & 0xff) * (1 + amount));
  const b = clamp((value & 0xff) * (1 + amount));

  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}
